"use client";

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button, { geistSans } from "@/components/atoms/buttons/Button";
import { isBelgianPublicHoliday } from "@/lib/holidays";
import { getFulfillmentHours } from "@/lib/fulfillment-hours";
import { cn, convertDate, getAddressAttributes, getCalendarLocale, getCheckoutBackRoute } from "@/lib/utils";
import { createPaymentLocalisedSchema, DeliveryAndBillingSchema } from "@/lib/validation/payment";
import Link from "next/link";
import { ChevronLeft, InfoIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useCheckoutStore } from "@/stores/checkout-store";
import { Locales } from "@/lib/i18n";
import RadioShipping from "@/components/atoms/radio-shipping";
import RadioButton from "@/components/atoms/radio-buttons";
import { __, getPermalink } from "@/lib/i18n/functions";
import { useCustomer } from "@/hooks/useCustomer";
import ShippingDetails from "@/components/templates/Checkout/components/shipping-details";
import BillingDetails from "@/components/templates/Checkout/components/billing-details";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { toast } from "@/components/atoms/toast";
import { resolveCheckoutFulfillment } from "@/lib/checkout-delivery";
import FulfillmentChoice from "@/components/templates/Checkout/components/fulfillment-choice";
import {
  BILLING_ADDRESS_REQUIREMENT_MESSAGE,
  isBillingAddressEligibleForAccountType,
} from "@/lib/billing-address-eligibility";

type DeliveryAndBillingFormProps = {
  locale: Locales;
  route: string;
  deliveryFaqUrl: string | undefined;
};

const scrollToError = (id: string, message?: string) => {
  if (message) toast.error({ title: message });
  const field = document.getElementById(id);
  if (field && "scrollIntoView" in field) {
    (field as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

const DeliveryAndBillingForm = ({ locale, route, deliveryFaqUrl }: DeliveryAndBillingFormProps) => {
  const router = useRouter();
  const [isDeliveryCalenderOpen, setIsDeliveryCalenderOpen] = useState(false);
  const [isRecoveryCalenderOpen, setIsRecoveryCalenderOpen] = useState(false);
  const { customer } = useCustomer();
  const customerAddresses = customer?.addresses;

  const setData = useCheckoutStore((state) => state.setData);
  const deliveryDate = useCheckoutStore((state) => state.deliveryDate);
  const recoveryDate = useCheckoutStore((state) => state.recoveryDate);
  const wantsDelivery = useCheckoutStore((state) => state.wantsDelivery);
  const deliveryAddress = useCheckoutStore((state) => state.deliveryAddress);
  const billingAddress = useCheckoutStore((state) => state.billingAddress);
  const eventDate = useCheckoutStore((state) => state.date);
  const accountType = useCheckoutStore((state) => state.accountType);
  const { deliveryAndBillingSchema } = createPaymentLocalisedSchema(locale);
  const initialFulfillment = resolveCheckoutFulfillment(wantsDelivery);
  const form = useForm<DeliveryAndBillingSchema>({
    resolver: zodResolver(deliveryAndBillingSchema),
    defaultValues: {
      wantsDelivery: initialFulfillment.wantsDelivery,
      deliveryDate: deliveryDate ? new Date(deliveryDate) : undefined,
      recoveryDate: recoveryDate ? new Date(recoveryDate) : undefined,
      wantsToRecoverItems: initialFulfillment.wantsRecovery,
      deliveryAddress: deliveryAddress || "",
      billingAddress: billingAddress || "",
      accountType: accountType || (customer?.company_name && "company") || "individual",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    clearErrors,
    formState: { errors },
    setError,
    setValue,
  } = form;

  const wantsDeliveryValue = watch("wantsDelivery");
  const fulfillment = resolveCheckoutFulfillment(wantsDeliveryValue);
  const accountTypeValue = watch("accountType");
  const deliveryDateValue = watch("deliveryDate");
  const recoveryDateValue = watch("recoveryDate");
  const shippingAddressValue = watch("deliveryAddress");
  const previousAccountTypeRef = useRef(accountTypeValue);

  useEffect(() => {
    if (wantsDeliveryValue) {
      setValue("wantsToRecoverItems", fulfillment.wantsRecovery, { shouldValidate: true });
      setData({
        wantsDelivery: fulfillment.wantsDelivery,
        wantsToRecoverItems: fulfillment.wantsRecovery,
      });
    }

    if (deliveryDateValue || recoveryDateValue) {
      setData({ deliveryDate: deliveryDateValue });
      setData({ recoveryDate: recoveryDateValue });
    }
  }, [
    wantsDeliveryValue,
    fulfillment.wantsDelivery,
    fulfillment.wantsRecovery,
    setData,
    setValue,
    deliveryDateValue,
    recoveryDateValue,
  ]);

  useEffect(() => {
    if (accountTypeValue) {
      const hasAccountTypeChanged =
        previousAccountTypeRef.current && previousAccountTypeRef.current !== accountTypeValue;

      setData({
        accountType: accountTypeValue,
        ...(hasAccountTypeChanged && { deliveryAddress: "", billingAddress: "" }),
      });
      if (hasAccountTypeChanged) {
        setValue("deliveryAddress", "");
        setValue("billingAddress", "");
      }
      previousAccountTypeRef.current = accountTypeValue;
      clearErrors("billingAddress");
      clearErrors("deliveryAddress");
    }
  }, [accountTypeValue, setData, clearErrors, setValue]);

  useEffect(() => {
    if (!customerAddresses) return;

    const deliveryExists =
      accountTypeValue === "company" || customerAddresses.some((addr) => addr.id === deliveryAddress);
    const billingExists = customerAddresses.some((addr) => addr.id === billingAddress);

    if (!deliveryExists && deliveryAddress) {
      setData({ deliveryAddress: "" });
      setValue("deliveryAddress", "");
    }

    if (!billingExists && billingAddress) {
      setData({ billingAddress: "" });
      setValue("billingAddress", "");
    }
  }, [accountTypeValue, customerAddresses, deliveryAddress, billingAddress, setData, setValue]);

  const onSubmit = (data: DeliveryAndBillingSchema) => {
    const submittedFulfillment = resolveCheckoutFulfillment(data.wantsDelivery);
    data = {
      ...data,
      wantsDelivery: submittedFulfillment.wantsDelivery,
      wantsToRecoverItems: submittedFulfillment.wantsRecovery,
    };

    if (!data.billingAddress) {
      const msg = __(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale);
      setError("billingAddress", { type: "required", message: msg });
      scrollToError("billingAddress", msg);
      return;
    }
    const { billingAddress: selectedBillingAddress } = getAddressAttributes(customer?.addresses, data.billingAddress);

    if (!selectedBillingAddress || !isBillingAddressEligibleForAccountType(selectedBillingAddress, data.accountType)) {
      const msg = __(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale);
      setError("billingAddress", { type: "required", message: msg });
      scrollToError("billingAddress", msg);
      return;
    }

    setData(data);
    router.push(getPermalink(locale, "checkout", "recap"));
  };

  const onError = (errors: typeof form.formState.errors) => {
    const entries = Object.entries(errors);
    if (entries.length === 0) return;

    const [firstKey, firstError] = entries[0];
    scrollToError(firstKey, firstError?.message);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit, onError)} className={cn("flex flex-col gap-5", geistSans.className)}>
        <div className="text-blue-brand font-cabinet-grotesk flex items-center gap-3 rounded-sm bg-[#0093D012] px-3 py-4.5">
          <InfoIcon className="text-blue-brand" size={15} />
          <p>
            {__("Select if you want Festi managed delivery and recovery of items.", locale)}{" "}
            {deliveryFaqUrl && (
              <Link className="font-medium underline underline-offset-1" href={`/${deliveryFaqUrl}`}>
                {__("See delivery conditions.", locale)}
              </Link>
            )}
          </p>
        </div>

        <div className="flex flex-col items-center gap-3.5 sm:flex-row lg:flex-col xl:flex-row">
          <FormField
            control={control}
            name="wantsDelivery"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.deliveryDate && !errors.wantsDelivery && "mb-5")}>
                <FulfillmentChoice locale={locale} value={field.value} onValueChange={field.onChange} />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="deliveryDate"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.wantsDelivery && !errors.deliveryDate && "mb-5")}>
                <div className="flex flex-col gap-3.5">
                  <FormLabel className="text-base font-medium">
                    {__("Items pickup date", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>
                  <Popover open={isDeliveryCalenderOpen} onOpenChange={setIsDeliveryCalenderOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          id={field.name}
                          onClick={() => setIsDeliveryCalenderOpen(true)}
                          variant="secondary"
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 [&>svg]:text-dark-primary hover:text-dark-primary text-primary-light flex h-12 w-full items-center justify-between gap-2 rounded-sm bg-white px-3.5 text-sm font-medium hover:bg-[#F4F4F499] focus-visible:ring-[2px]"
                          iconRightName="CalendarIcon"
                        >
                          {deliveryDateValue ? convertDate(deliveryDateValue) : __("Select a date", locale)}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-full border-none p-0" align="start">
                      <Calendar
                        isWeekendDisabled
                        disableHolidays
                        onClose={() => setIsDeliveryCalenderOpen(false)}
                        title={__("Delivery Date", locale)}
                        selected={deliveryDateValue}
                        disabled={(date) => {
                          if (isBelgianPublicHoliday(date)) {
                            return true;
                          }

                          if (eventDate?.from) {
                            return date > new Date(eventDate.from);
                          }
                          return false;
                        }}
                        setLocale={getCalendarLocale(locale)}
                        onSelect={(date: unknown) => {
                          if (date instanceof Date) {
                            field.onChange(date);
                          } else if (date && typeof date === "object" && "from" in date) {
                            field.onChange(date.from);
                          } else {
                            field.onChange(date);
                          }
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <p className="text-sm font-semibold">
          {__("Time", locale)} : {getFulfillmentHours("outbound", fulfillment.owner)}
        </p>

        <div className="flex flex-col items-center gap-3.5 sm:flex-row lg:flex-col xl:flex-row">
          <FormField
            control={form.control}
            name="recoveryDate"
            render={({ field }) => (
              <FormItem className="w-full gap-1">
                <div className="flex flex-col gap-3.5">
                  <FormLabel className="text-base font-medium">
                    {__("Items return date", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>
                  <Popover open={isRecoveryCalenderOpen} onOpenChange={setIsRecoveryCalenderOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          id={field.name}
                          variant="secondary"
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 [&>svg]:text-dark-primary hover:text-dark-primary text-primary-light flex h-12 w-full items-center justify-between gap-2 rounded-sm bg-white px-3.5 text-sm font-medium hover:bg-[#F4F4F499] focus-visible:ring-[2px] disabled:cursor-not-allowed!"
                          iconRightName="CalendarIcon"
                        >
                          {recoveryDateValue ? convertDate(recoveryDateValue) : __("Select a date", locale)}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-full border-none p-0" align="start">
                      <Calendar
                        isWeekendDisabled
                        onClose={() => setIsRecoveryCalenderOpen(false)}
                        title={__("Recovery date", locale)}
                        selected={recoveryDateValue}
                        disabled={(date) => {
                          if (isBelgianPublicHoliday(date)) {
                            return true;
                          }

                          if (eventDate?.to) {
                            return date < new Date(eventDate.to);
                          }
                          return false;
                        }}
                        disableHolidays
                        onSelect={(date: unknown) => {
                          if (date instanceof Date) {
                            field.onChange(date);
                          } else if (date && typeof date === "object" && "from" in date) {
                            field.onChange(date.from);
                          } else {
                            field.onChange(date);
                          }
                        }}
                        setLocale={getCalendarLocale(locale)}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <p className="text-sm font-semibold">
          {__("Time", locale)} :{" "}
          {getFulfillmentHours("return", fulfillment.owner)}
        </p>

        <hr className="border-primary-light/50" />
        <h2
          id="deliveryAddress"
          className={cn(
            "font-cabinet-grotesk text-2xl font-medium md:text-3xl",
            errors.deliveryAddress && "text-pink-primary"
          )}
        >
          {__("Shipping address", locale)}
          {fulfillment.requiresShippingAddress && <span className="text-pink-primary">*</span>}
        </h2>
        <ShippingDetails customerAddresses={customerAddresses} locale={locale} accountTypeValue={accountTypeValue} />
        <hr className="border-primary-light/50" />
        <FormField
          control={control}
          name="accountType"
          render={({ field }) => (
            <FormItem className="w-full gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("Account Type", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <RadioShipping
                  locale={locale}
                  name="accountType"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="max-w-none flex-row gap-4 max-sm:hidden lg:flex-col xl:flex-row"
                  items={[
                    { value: "company", content: { title: __("I am a business", locale) } },
                    { value: "individual", content: { title: __("I am an individual", locale) } },
                  ]}
                />
                <RadioButton
                  name="account-type"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="w-full flex-col items-start gap-2.5 min-sm:hidden"
                  options={[
                    { value: "company", label: __("I am a business", locale) },
                    { value: "individual", label: __("I am an individual", locale) },
                  ]}
                />
              </div>

              <FormMessage />
            </FormItem>
          )}
        />
        <hr className="border-primary-light/50" />
        <h2
          id="billingAddress"
          className={cn(
            "font-cabinet-grotesk text-2xl font-medium md:text-3xl",
            errors.billingAddress && "text-pink-primary"
          )}
        >
          {__("Billing details", locale)}
          <span className="text-pink-primary">*</span>
        </h2>
        <BillingDetails
          shippingAddressValue={shippingAddressValue}
          accountTypeValue={accountTypeValue}
          customerAddresses={customerAddresses}
          locale={locale}
        />
        <Link
          href={getCheckoutBackRoute(locale, route)}
          className="text-blue-brand flex w-full items-center justify-center gap-2.5 pr-1 text-start font-medium min-md:hidden"
        >
          <ChevronLeft size={24} aria-hidden="true" />
          {__("Return to the previous step", locale)}
        </Link>
        <Button
          type="submit"
          className="h-12 transition-transform duration-150 ease-in-out active:scale-95 min-sm:ml-auto min-sm:h-[60px]"
        >
          {__("Validate my quote", locale)}
        </Button>
      </form>
    </Form>
  );
};

export default DeliveryAndBillingForm;
